import { createAsyncThunk } from "@reduxjs/toolkit";
import {GetWorkflowList, CreateWorkflow, GetWorkflow, GetFieldGroup,DeleteProcess, UpdateWorkflow, UpdateGlobalWorkflowSettings, GetRoles} from '../service'
import { getStorage } from "../../../utilities/browserStorage";
import i18n from "../../../utilities/i18n";
import { CREATE_DEFAULT_ERROR, CREATE_ERROR_MAPPINGS, DELETE_DEFAULT_ERROR, UPDATE_DEFAULT_ERROR, UPDATE_ERROR_MAPPINGS } from "../constants";
import { checkForValidations, EscalationSettingsChange } from "./DataProcessor";

// Fetch all workflows
export const fetchWorkflows = createAsyncThunk(
    "workflow/fetchWorkflows",
    async (_, { rejectWithValue }) => {
        try {
            const practice=getStorage('practice');
            const response = await GetWorkflowList(practice);
            return response.data; // Assuming your api utility returns data in .data
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to fetch workflow");
        }
    }
);

// Creat new workflow
export const createWorkFlow = createAsyncThunk(
    "workflow/createWorkFlow",
    async ({ updatedData, index }, { rejectWithValue,dispatch }) => {
        try {

            const validation=checkForValidations(updatedData);

            if(validation.has_error){
                return rejectWithValue({message:validation.error_message,errors:validation.errors,index:index})
            }

            const practice = getStorage('practice');
            updatedData={
                ...updatedData,
                practice:practice
            }
            const response = await CreateWorkflow(updatedData);

            if (response.status === 201) {
                dispatch(fetchWorkflows());
                return { data: updatedData, index,id:response.data.workflow_id ??  "" };
            }
            if (response.status === 400) {
                const messageKey = CREATE_ERROR_MAPPINGS[response.data?.message] || CREATE_DEFAULT_ERROR;
                return rejectWithValue({message:i18n.t(messageKey),errors:[],index:""});
            }

            return rejectWithValue({message:i18n.t(CREATE_DEFAULT_ERROR),errors:[],index:""});
        } catch (error) {
            return rejectWithValue({message:i18n.t(CREATE_DEFAULT_ERROR),errors:[],index:""});
        }
    }
);

export const getWorkFlow = createAsyncThunk(
    "workflow/getWorkFlow", 
    async ({id,type},{rejectWithValue}) => {
        try {
            const response = await GetWorkflow(id);
            if(response.data){
                return {data:response.data,type:type,id}
            }
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to getWorkFlow")
        }
    }
)

export const getFieldList = createAsyncThunk(
    "workflow/getFieldList",
    async (_,{rejectWithValue}) => {
        try{
            const response = await GetFieldGroup();
            return response.data
            
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to list fields.")
        }
    }
)

export const listWorkflowRoles = createAsyncThunk(
    "workflow/listWorkflowRoles",
    async ({rejectWithValue}) => {
        const practicePK = getStorage('practice');
        try{
            const response = await GetRoles(practicePK)
            return response.data
            
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to list roles.")
        }
    }
)

export const DeleteProcessName = createAsyncThunk(
    "workflow/deleteWorkflow",
    async (payload, { rejectWithValue,dispatch }) => {
        try {
            const response = await DeleteProcess(payload);

            if(response.status==204){
                dispatch(fetchWorkflows());
                return payload;
            }

            return rejectWithValue(i18n.t(DELETE_DEFAULT_ERROR));
        } catch (error) {
            return rejectWithValue(i18n.t(DELETE_DEFAULT_ERROR));
        }
    }
);

export const updateWorkFlow = createAsyncThunk(
    "workflow/updateWorkFlow",
    async ({ updatedData, id,index,stage }, { rejectWithValue }) => {
        try {

            const validation=checkForValidations(updatedData);

            if(validation.has_error){
                return rejectWithValue({message:validation.error_message,errors:validation.errors,index:index})
            }

            const practice=getStorage('practice');
            updatedData={
                ...updatedData,
                practice:practice
            }
            const response = await UpdateWorkflow(updatedData,id);

            if (response.status === 200) {
                return { data: updatedData, id, index,stage };
            }
            if (response.status === 400) {
                const messageKey = UPDATE_ERROR_MAPPINGS[response.data?.message] || UPDATE_DEFAULT_ERROR;
                return rejectWithValue({message:i18n.t(messageKey),errors:[],index:""});
            }

            return rejectWithValue({message:i18n.t(UPDATE_DEFAULT_ERROR),errors:[],index:""});
        } catch (error) {
            return rejectWithValue({message:i18n.t(UPDATE_DEFAULT_ERROR),errors:[],index:""});
        }
    }
);


export const reOrderWorkFLow = createAsyncThunk(
    "workflow/reorder",
    async ({ hoverIndex , dragIndex ,row }, { rejectWithValue }) => {
        try {
            const practice=getStorage('practice');
            let updatedData={
                process_name:row.process_name ?? "",
                id:row.id ?? "",
                role_ids: row.role_ids ?? [],
                field_group_ids:row.field_group_ids ?? [],

                sequence_num: hoverIndex + 1,
                practice:practice
            }

            const response = await UpdateWorkflow(updatedData,row.id ?? "");

            if (response.status === 200) {
                return { hoverIndex , dragIndex};
            }
            if (response.status === 400) {
                const messageKey = UPDATE_ERROR_MAPPINGS[response.data?.message] || UPDATE_DEFAULT_ERROR;
                return rejectWithValue(i18n.t(messageKey));
            }

            return rejectWithValue(i18n.t(UPDATE_DEFAULT_ERROR));
        } catch (error) {
            return rejectWithValue(i18n.t(UPDATE_DEFAULT_ERROR));
        }
    }
);


export const saveGlobalWorkflowSettings = createAsyncThunk(
    "workflow/saveGlobalWorkflowSettings",
    async ({ allowMultipleWorkflows,enableAuditing }, { rejectWithValue,dispatch }) => {
        try {
            const practice = getStorage('practice');
            const data = {
                practice_id: practice,
                follow_sequential_workflow: !allowMultipleWorkflows,
                enable_auditing_workflow: enableAuditing ?? false
            };
            const response = await UpdateGlobalWorkflowSettings(data);
            if (response.status === 200 || response.status === 201) {
                dispatch(fetchWorkflows());
                return {
                    allowMultipleWorkflows,
                };
            }
            return rejectWithValue(i18n.t('work_flow.common_message.update_failed_response'));
        } catch (error) {
            return rejectWithValue(i18n.t('work_flow.common_message.update_failed_response'));
        }
    }
);


export const saveEscalationSettings = createAsyncThunk(
    "workflow/saveEscalationSettings",
    async (_, { rejectWithValue,getState,dispatch}) => {
        try {
            const practice = getStorage('practice');
            const state=getState();
            const {workflow}=state;
            const {escalationSettings,escalationSettingsDuplicate}=workflow;
            const changedData=EscalationSettingsChange(escalationSettings,escalationSettingsDuplicate);

            if (Object.keys(changedData).length === 0) {
                return rejectWithValue(i18n.t('work_flow.api_error.no_changes_made'));
            }

            let data={
                practice_id: practice,
                ...changedData
            }

            const response = await UpdateGlobalWorkflowSettings(data);
            if (response.status === 200 || response.status === 201) {
                dispatch(fetchWorkflows());
                return response.data;
            }
            return rejectWithValue(i18n.t('work_flow.common_message.update_failed_response'));
        } catch (error) {
            const errorMessage = error?.response?.data?.message || i18n.t('work_flow.common_message.escalation_settings_update_failed');
            return rejectWithValue(errorMessage);
        }
    }
);